import { BaseNodeCommand, NodeChangeRecord } from './base/BaseNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { computeDockedLayoutUpdatesForTable } from '../../domains/diagram/shapes/table/tableDockLayout.utils';
import { Tracker } from '../../domains/analytics';
import { TABLE_ADVANCED } from '../../domains/analytics/constants/MetricEvents';

type TableGridField = 'rowCount' | 'columnCount' | 'showHeader' | 'showHeaderColumn' | 'autoResizeCells';

export interface UpdateTableGridParams {
  field: TableGridField;
  value: number | boolean;
}

const MIN_CELL = 20;
const DEFAULT_HEADER = 32;

const clampCount = (value: unknown, fallback: number, min = 1): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

const ensureArray = (arr: unknown, count: number, fallbackTotal: number): number[] => {
  if (Array.isArray(arr) && arr.length === count && arr.every((n) => typeof n === 'number' && Number.isFinite(n) && n > 0)) {
    return arr as number[];
  }
  const even = Math.max(MIN_CELL, fallbackTotal / Math.max(1, count));
  return Array(count).fill(even);
};

type Axis = 'row' | 'column';

function shiftCellKeyMap(map: Record<string, any>, axis: Axis, delta: 1 | -1): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(map ?? {})) {
    const [rStr, cStr] = k.split(',');
    const r = Number(rStr);
    const c = Number(cStr);
    if (!Number.isFinite(r) || !Number.isFinite(c)) continue;
    if (axis === 'row') {
      const nr = r + delta;
      if (nr < 0) continue;
      out[`${nr},${c}`] = v;
    } else {
      const nc = c + delta;
      if (nc < 0) continue;
      out[`${r},${nc}`] = v;
    }
  }
  return out;
}

function shiftMergedRegions(regions: unknown, axis: Axis, delta: 1 | -1): Array<{ startRow: number; startCol: number; endRow: number; endCol: number }> {
  if (!Array.isArray(regions)) return [];
  const out: Array<{ startRow: number; startCol: number; endRow: number; endCol: number }> = [];
  for (const r of regions) {
    if (!r || typeof r !== 'object') continue;
    const mr = r as any;
    const startRow = Number(mr.startRow);
    const endRow = Number(mr.endRow);
    const startCol = Number(mr.startCol);
    const endCol = Number(mr.endCol);
    if (![startRow, endRow, startCol, endCol].every((n) => Number.isFinite(n))) continue;
    if (axis === 'row') {
      const ns = startRow + delta;
      const ne = endRow + delta;
      if (ns < 0 || ne < 0) continue;
      out.push({ startRow: ns, endRow: ne, startCol, endCol });
    } else {
      const ns = startCol + delta;
      const ne = endCol + delta;
      if (ns < 0 || ne < 0) continue;
      out.push({ startRow, endRow, startCol: ns, endCol: ne });
    }
  }
  return out;
}

function shiftIndices(indices: unknown, delta: 1 | -1): number[] {
  if (!Array.isArray(indices)) return [];
  const next: number[] = [];
  for (const v of indices) {
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    const nn = n + delta;
    if (nn < 0) continue;
    next.push(nn);
  }
  return Array.from(new Set(next)).sort((a, b) => a - b);
}

export class UpdateTableGridCommand extends BaseNodeCommand {
  constructor(private nodeIds: string[], private params: UpdateTableGridParams) {
    super('Update Table Grid', `Update ${params.field}`);
  }

  protected doExecute(): NodeChangeRecord[] {
    const records: NodeChangeRecord[] = [];
    const mutationManager = this.getMutationManager() as any;

    const batchUpdates: Array<{ nodeId: string; changes: any }> = [];

    for (const nodeId of this.nodeIds) {
      const node = mutationManager.getNode(nodeId) as any;
      if (!node) continue;
      if (node.type !== 'table') continue; // Only operate on table shapes
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;

      const currentRowCount = clampCount(attrs.rowCount, 3);
      const currentColCount = clampCount(attrs.columnCount, 3);
      const currentShowHeader = attrs.showHeader !== false;
      const headerHeight = typeof attrs.headerHeight === 'number' && Number.isFinite(attrs.headerHeight) && attrs.headerHeight > 0
        ? Math.max(MIN_CELL, attrs.headerHeight)
        : DEFAULT_HEADER;

      const width = (node.width ?? node.w ?? 240) as number;
      const height = (node.height ?? node.h ?? 180) as number;

      const currentBodyHeight = Math.max(MIN_CELL * currentRowCount, height - (currentShowHeader ? headerHeight : 0));
      const rowHeights = ensureArray(attrs.rowHeights, currentRowCount, currentBodyHeight);
      const colWidths = ensureArray(attrs.columnWidths, currentColCount, width);

      const changes: any = { attrs: {} as Record<string, unknown> };

      if (this.params.field === 'rowCount' && typeof this.params.value === 'number') {
        const target = clampCount(this.params.value, currentRowCount);
        const nextHeights = [...rowHeights];
        let nextHeight = height;
        const delta = target - currentRowCount;

        if (delta > 0) {
          for (let i = 0; i < delta; i += 1) {
            const base = nextHeights.length > 0 ? nextHeights[nextHeights.length - 1] : MIN_CELL;
            const add = Math.max(MIN_CELL, base);
            nextHeights.push(add);
            nextHeight += add;
          }
        } else if (delta < 0) {
          for (let i = 0; i < Math.abs(delta); i += 1) {
            if (nextHeights.length <= 1) break;
            const removed = nextHeights.pop() ?? MIN_CELL;
            nextHeight -= removed;
          }
          const minTotal = (currentShowHeader ? headerHeight : 0) + (Math.max(1, target) * MIN_CELL);
          nextHeight = Math.max(minTotal, nextHeight);
        }

        changes.attrs.rowCount = target;
        changes.attrs.rowHeights = nextHeights;
        changes.height = nextHeight;
        changes.h = nextHeight;
      }

      if (this.params.field === 'columnCount' && typeof this.params.value === 'number') {
        const target = clampCount(this.params.value, currentColCount);
        const nextWidths = [...colWidths];
        let nextWidth = width;
        const delta = target - currentColCount;

        if (delta > 0) {
          for (let i = 0; i < delta; i += 1) {
            const base = nextWidths.length > 0 ? nextWidths[nextWidths.length - 1] : MIN_CELL;
            const add = Math.max(MIN_CELL, base);
            nextWidths.push(add);
            nextWidth += add;
          }
        } else if (delta < 0) {
          for (let i = 0; i < Math.abs(delta); i += 1) {
            if (nextWidths.length <= 1) break;
            const removed = nextWidths.pop() ?? MIN_CELL;
            nextWidth -= removed;
          }
          const minTotal = Math.max(1, target) * MIN_CELL;
          nextWidth = Math.max(minTotal, nextWidth);
        }

        changes.attrs.columnCount = target;
        changes.attrs.columnWidths = nextWidths;
        changes.width = nextWidth;
        changes.w = nextWidth;
      }

      if (this.params.field === 'showHeader' && typeof this.params.value === 'boolean') {
        const nextShow = this.params.value;
        let nextHeight = height;
        if (nextShow && !currentShowHeader) {
          nextHeight += headerHeight;
        } else if (!nextShow && currentShowHeader) {
          nextHeight -= headerHeight;
        }
        const minTotal = (nextShow ? headerHeight : 0) + (currentRowCount * MIN_CELL);
        nextHeight = Math.max(minTotal, nextHeight);

        changes.attrs.showHeader = nextShow;
        changes.attrs.headerHeight = headerHeight;
        const prevHeaderRows = Array.isArray((attrs as any).headerRowIndices) ? ([...(attrs as any).headerRowIndices] as number[]) : [];

        // Toggling header row should not re-index existing body content.
        // We shift render-space maps/regions when the header row is inserted/removed.
        if (nextShow && !currentShowHeader) {
          changes.attrs.cellText = shiftCellKeyMap(((attrs as any).cellText ?? {}) as Record<string, any>, 'row', 1);
          changes.attrs.cellStyles = shiftCellKeyMap(((attrs as any).cellStyles ?? {}) as Record<string, any>, 'row', 1);
          changes.attrs.cellLayouts = shiftCellKeyMap(((attrs as any).cellLayouts ?? {}) as Record<string, any>, 'row', 1);
          changes.attrs.mergedRegions = shiftMergedRegions((attrs as any).mergedRegions, 'row', 1);
          changes.attrs.headerRowIndices = Array.from(new Set([0, ...shiftIndices(prevHeaderRows, 1)])).sort((a, b) => a - b);
        } else if (!nextShow && currentShowHeader) {
          // Removing header row drops row 0 content and shifts the rest up.
          const dropRow0 = (map: Record<string, any>) => {
            const out: Record<string, any> = {};
            for (const [k, v] of Object.entries(map ?? {})) {
              const [rStr, cStr] = k.split(',');
              const r = Number(rStr);
              const c = Number(cStr);
              if (!Number.isFinite(r) || !Number.isFinite(c)) continue;
              if (r === 0) continue;
              out[`${r - 1},${c}`] = v;
            }
            return out;
          };
          changes.attrs.cellText = dropRow0(((attrs as any).cellText ?? {}) as Record<string, any>);
          changes.attrs.cellStyles = dropRow0(((attrs as any).cellStyles ?? {}) as Record<string, any>);
          changes.attrs.cellLayouts = dropRow0(((attrs as any).cellLayouts ?? {}) as Record<string, any>);
          changes.attrs.mergedRegions = shiftMergedRegions((attrs as any).mergedRegions, 'row', -1);
          changes.attrs.headerRowIndices = shiftIndices(prevHeaderRows.filter((i) => i !== 0), -1);
        } else {
          // No structural change; just ensure header row styling includes 0 when enabled.
          changes.attrs.headerRowIndices = nextShow ? Array.from(new Set([0, ...prevHeaderRows])).sort((a, b) => a - b) : prevHeaderRows.filter((i) => i !== 0);
        }
        changes.height = nextHeight;
        changes.h = nextHeight;
      }

      if (this.params.field === 'showHeaderColumn' && typeof this.params.value === 'boolean') {
        const nextShowCol = this.params.value;
        const prevHeaderCols = Array.isArray((attrs as any).headerColIndices) ? ([...(attrs as any).headerColIndices] as number[]) : [];

        // Header column behaves like a structural add/remove (insert/remove col 0) to match header-row behavior.
        if (nextShowCol && attrs.showHeaderColumn !== true) {
          const nextWidths = [Math.max(MIN_CELL, (colWidths[0] ?? MIN_CELL)), ...colWidths];
          const nextWidth = width + nextWidths[0];
          changes.attrs.showHeaderColumn = true;
          changes.attrs.columnCount = currentColCount + 1;
          changes.attrs.columnWidths = nextWidths;
          changes.width = nextWidth;
          changes.w = nextWidth;
          changes.attrs.cellText = shiftCellKeyMap(((attrs as any).cellText ?? {}) as Record<string, any>, 'column', 1);
          changes.attrs.cellStyles = shiftCellKeyMap(((attrs as any).cellStyles ?? {}) as Record<string, any>, 'column', 1);
          changes.attrs.cellLayouts = shiftCellKeyMap(((attrs as any).cellLayouts ?? {}) as Record<string, any>, 'column', 1);
          changes.attrs.mergedRegions = shiftMergedRegions((attrs as any).mergedRegions, 'column', 1);
          changes.attrs.headerColIndices = Array.from(new Set([0, ...shiftIndices(prevHeaderCols, 1)])).sort((a, b) => a - b);
        } else if (!nextShowCol && attrs.showHeaderColumn === true) {
          if (currentColCount <= 1) {
            changes.attrs.showHeaderColumn = false;
            changes.attrs.headerColIndices = prevHeaderCols.filter((i) => i !== 0);
          } else {
            const removedWidth = colWidths[0] ?? MIN_CELL;
            const nextWidths = colWidths.slice(1);
            const nextWidth = Math.max(MIN_CELL * Math.max(1, currentColCount - 1), width - removedWidth);
            changes.attrs.showHeaderColumn = false;
            changes.attrs.columnCount = currentColCount - 1;
            changes.attrs.columnWidths = nextWidths;
            changes.width = nextWidth;
            changes.w = nextWidth;
            const dropCol0 = (map: Record<string, any>) => {
              const out: Record<string, any> = {};
              for (const [k, v] of Object.entries(map ?? {})) {
                const [rStr, cStr] = k.split(',');
                const r = Number(rStr);
                const c = Number(cStr);
                if (!Number.isFinite(r) || !Number.isFinite(c)) continue;
                if (c === 0) continue;
                out[`${r},${c - 1}`] = v;
              }
              return out;
            };
            changes.attrs.cellText = dropCol0(((attrs as any).cellText ?? {}) as Record<string, any>);
            changes.attrs.cellStyles = dropCol0(((attrs as any).cellStyles ?? {}) as Record<string, any>);
            changes.attrs.cellLayouts = dropCol0(((attrs as any).cellLayouts ?? {}) as Record<string, any>);
            changes.attrs.mergedRegions = shiftMergedRegions((attrs as any).mergedRegions, 'column', -1);
            changes.attrs.headerColIndices = shiftIndices(prevHeaderCols.filter((i) => i !== 0), -1);
          }
        } else {
          changes.attrs.showHeaderColumn = nextShowCol;
          changes.attrs.headerColIndices = nextShowCol ? Array.from(new Set([0, ...prevHeaderCols])).sort((a, b) => a - b) : prevHeaderCols.filter((i) => i !== 0);
        }
      }

      if (this.params.field === 'autoResizeCells' && typeof this.params.value === 'boolean') {
        changes.attrs.autoResizeCells = this.params.value;
      }

      if (Object.keys(changes.attrs).length === 0) continue;

      const latest = mutationManager.getNode(nodeId);
      changes.x = (latest as any)?.x ?? node.x ?? 0;
      changes.y = (latest as any)?.y ?? node.y ?? 0;

      batchUpdates.push({ nodeId, changes });

      const previewAttrs = { ...(attrs as any), ...(changes.attrs as any) };
      const preview: any = { ...node, ...changes, attrs: previewAttrs };

      // If we inserted/removed header row/col, docked nodes need their cellKey shifted too.
      const storeNodes = useDiagramStore.getState().nodes;
      const dockedToTable = storeNodes.filter((n) => (n as any)?.attrs?.tableDock?.tableId === nodeId);
      const shiftDock = (axis: Axis, delta: 1 | -1) => {
        const updates: Array<{ nodeId: string; changes: any }> = [];
        for (const dn of dockedToTable) {
          const dock = (dn as any)?.attrs?.tableDock;
          const key = dock?.cellKey as string | undefined;
          if (!dock || typeof key !== 'string') continue;
          const [rStr, cStr] = key.split(',');
          const r = Number(rStr);
          const c = Number(cStr);
          if (!Number.isFinite(r) || !Number.isFinite(c)) continue;
          if (axis === 'row') {
            const nr = r + delta;
            if (nr < 0) continue;
            updates.push({ nodeId: dn.id, changes: { attrs: { tableDock: { ...dock, cellKey: `${nr},${c}` } } } });
          } else {
            const nc = c + delta;
            if (nc < 0) continue;
            updates.push({ nodeId: dn.id, changes: { attrs: { tableDock: { ...dock, cellKey: `${r},${nc}` } } } });
          }
        }
        return updates;
      };

      const dockKeyShiftUpdates: Array<{ nodeId: string; changes: any }> = [];
      if (this.params.field === 'showHeader' && typeof this.params.value === 'boolean') {
        if (this.params.value && !currentShowHeader) dockKeyShiftUpdates.push(...shiftDock('row', 1));
        if (!this.params.value && currentShowHeader) dockKeyShiftUpdates.push(...shiftDock('row', -1));
      }
      if (this.params.field === 'showHeaderColumn' && typeof this.params.value === 'boolean') {
        if (this.params.value && attrs.showHeaderColumn !== true) dockKeyShiftUpdates.push(...shiftDock('column', 1));
        if (!this.params.value && attrs.showHeaderColumn === true) dockKeyShiftUpdates.push(...shiftDock('column', -1));
      }

      const previewNodes = storeNodes.map((n) => {
        const u = dockKeyShiftUpdates.find((x) => x.nodeId === n.id);
        if (!u) return n;
        return { ...n, attrs: { ...(n as any).attrs, ...(u.changes.attrs ?? {}) } } as any;
      });

      dockKeyShiftUpdates.forEach((u) => batchUpdates.push(u));
      const dockedUpdates = computeDockedLayoutUpdatesForTable(preview, previewNodes);
      dockedUpdates.forEach((u) => batchUpdates.push(u));
    }

    if (batchUpdates.length > 1 && typeof mutationManager.applyBatchNodeChanges === 'function') {
      const batchRecords = mutationManager.applyBatchNodeChanges(batchUpdates);
      if (batchRecords && batchRecords.length > 0) {
        const headerToggled =
          this.params.field === 'showHeader' || this.params.field === 'showHeaderColumn';
        if (headerToggled) {
          Tracker.track(TABLE_ADVANCED.HEADER_TOGGLED, {
            field: this.params.field,
            value: this.params.value,
            tableCount: this.nodeIds.length,
          });
        }
      }
      records.push(...(batchRecords ?? []));
    } else if (batchUpdates.length === 1) {
      const { nodeId, changes } = batchUpdates[0];
      const record = mutationManager.applyNodeChange(nodeId, changes);
      if (record) {
        const headerToggled =
          this.params.field === 'showHeader' || this.params.field === 'showHeaderColumn';
        if (headerToggled) {
          Tracker.track(TABLE_ADVANCED.HEADER_TOGGLED, {
            field: this.params.field,
            value: this.params.value,
            tableCount: 1,
          });
        }
        records.push(record);
      }
    }

    return records;
  }
}
